import { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Briefcase, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, Badge, DataTable, Pagination, useAuth } from '@aaas/shared';
import type { DataTableColumn } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { usePositionDetailQuery } from '../../hooks/usePositionQueries';
import { useCVListQuery } from '../../hooks/useCVQueries';
import type { CV, CVProcessingStatus } from '../../types';
import { PROCESSING_STATUS_CONFIG } from '../../types';

// Status badge component
function StatusBadge({ status }: { status: CVProcessingStatus }) {
  const config = PROCESSING_STATUS_CONFIG[status];
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          config.color === 'gray'
            ? 'bg-gray-500'
            : config.color === 'blue'
              ? 'bg-blue-500'
              : config.color === 'green'
                ? 'bg-green-500'
                : 'bg-red-500'
        }`}
      />
      {config.label}
    </span>
  );
}

// Chip component
function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      {label}
    </span>
  );
}

export function PositionDetailPage() {
  const { t } = useTranslation('ats');
  const { positionId } = useParams<{ positionId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { page, size, sortBy, order, setPage, setSize, setSort } = useUrlPagination();

  // Fetch position details
  const {
    data: position,
    isLoading: positionLoading,
    error: positionError,
  } = usePositionDetailQuery({
    positionId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!positionId,
  });

  // Fetch CVs linked to this position using unified CV list query with positionId filter
  const {
    data: cvResponse,
    isLoading: cvsLoading,
    error: cvsError,
  } = useCVListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    positionIds: positionId ? [positionId] : undefined,
    enabled: !!user && !!currentWorkspace && !!positionId,
    refetchInterval: 5000,
  });

  // Extract data from response
  const cvs = cvResponse?.data?.items || [];
  const totalItems = cvResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleBack = () => {
    navigate('/ats/positions');
  };

  const handleCVClick = useCallback(
    (record: CV) => {
      navigate(`/ats/cvs/${record.id}`);
    },
    [navigate]
  );

  // Define table columns (same as CVListPage)
  const columns: DataTableColumn<CV>[] = [
    {
      key: 'name',
      title: t('position.detail.columns.name'),
      sortable: true,
      render: (value: string, record: CV) => (
        <button
          onClick={() => handleCVClick(record)}
          className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'processing_status',
      title: t('position.detail.columns.status'),
      width: 120,
      render: (value: CVProcessingStatus) => <StatusBadge status={value} />,
    },
    {
      key: 'pools',
      title: t('position.detail.columns.pools'),
      render: (_value, record: CV) => {
        if (!record.pools || record.pools.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.pools.map((p) => (
              <Chip key={p.id} label={p.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'positions',
      title: t('position.detail.columns.positions'),
      render: (_value, record: CV) => {
        if (!record.positions || record.positions.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.positions.map((p) => (
              <Chip key={p.id} label={p.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'jobs',
      title: t('position.detail.columns.jobs'),
      render: (_value, record: CV) => {
        if (!record.jobs || record.jobs.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.jobs.map((job) => (
              <Chip
                key={job.id}
                label={`${job.code}${job.campaign_name ? ` (${job.campaign_name})` : ''}`}
              />
            ))}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      title: t('position.detail.columns.created'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const loading = positionLoading || cvsLoading;
  const error = positionError || cvsError;

  if (loading && !position) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  if (positionError) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('position.detail.back')}
        </button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {positionError instanceof Error ? positionError.message : t('position.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!position) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('position.detail.back')}
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-green-600" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{position.name}</h1>
                <Badge
                  className={
                    position.status === 'active'
                      ? 'bg-green-100 text-green-800 hover:bg-green-100'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                  }
                >
                  {position.status === 'active' ? t('positionConfig.statusActive') : t('positionConfig.statusInactive')}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 font-mono">{position.code}</p>
            </div>
          </div>
        </div>

        {/* Position metadata */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          {position.description && (
            <div>
              <span className="text-gray-400">{t('position.detail.description')}</span>{' '}
              <span className="font-medium">{position.description}</span>
            </div>
          )}
          <div>
            <span className="text-gray-400">{t('position.detail.totalCvs')}</span>{' '}
            <span className="font-medium">{totalItems}</span>
          </div>
          <div>
            <span className="text-gray-400">{t('position.detail.created')}</span>{' '}
            <span className="font-medium">
              {new Date(position.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('position.detail.loadCvsFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={cvs}
          loading={cvsLoading}
          rowKey={(record) => record.id}
          emptyMessage={t('position.detail.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>
    </div>
  );
}
